/**
 * CORREÇÃO DE FORMA DE RECEBIMENTO — Financeiro
 *
 * FEATURE 25/07/2026: operador bateu a venda do PDV na forma errada (ex.: recebeu no
 * cartão e lançou como dinheiro). Antes só dava pra cancelar a venda e refazer.
 *
 * A troca propaga pros 3 lugares onde a forma vive (pedido / título / lançamento +
 * saldo bancário) — ver correcao_forma_routes.py. Só entre formas À VISTA.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowRightLeft, Banknote, Loader2, Lock, AlertTriangle, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'

const hoje = () => new Date().toISOString().slice(0, 10)

type Linha = { indice: number; forma: string; valor: number; parcelas: number; corrigivel: boolean }
type Venda = {
  pedido_id: number; numero: number; data: string | null; cliente: string
  total: number; status: string; sessao_id: number | null; sessao_status: string | null
  pagamentos: Linha[]
}
type Forma = { forma: string; label: string; conta_nome: string | null; entra_na_gaveta: boolean }

export default function CorrecaoFormaRecebimentoPage() {
  const qc = useQueryClient()
  const [inicio, setInicio] = useState(hoje())
  const [fim, setFim] = useState(hoje())
  const [busca, setBusca] = useState('')
  const [alvo, setAlvo] = useState<{ venda: Venda; linha: Linha } | null>(null)
  const [formaNova, setFormaNova] = useState('')
  const [motivo, setMotivo] = useState('')
  const [senha, setSenha] = useState('')

  const LIMITE = 200
  const buscaNumero = /^\d+$/.test(busca.trim())

  const { data: vendas = [], isLoading } = useQuery<Venda[]>({
    queryKey: ['correcao-forma-vendas', inicio, fim, busca],
    queryFn: async () => (await api.get('/financeiro/correcao-forma/vendas', {
      params: { inicio, fim, busca: busca.trim() || undefined, limite: LIMITE },
    })).data,
  })
  // A lista traz as N mais recentes. Sem avisar, "não achei" vira "não existe" — que foi
  // exatamente o que aconteceu com a venda 555 (379 vendas mais novas que ela).
  const truncado = vendas.length >= LIMITE

  const { data: formas = [] } = useQuery<Forma[]>({
    queryKey: ['correcao-forma-formas'],
    queryFn: async () => (await api.get('/financeiro/correcao-forma/formas')).data,
  })

  const rotulo = (f: string) => formas.find(x => x.forma === f)?.label || f

  const fecharModal = () => {
    setAlvo(null); setFormaNova(''); setMotivo(''); setSenha('')
  }

  const corrigir = useMutation({
    mutationFn: async () => (await api.post(`/financeiro/correcao-forma/${alvo!.venda.pedido_id}`, {
      indice: alvo!.linha.indice,
      forma_nova: formaNova,
      motivo: motivo.trim(),
      senha,
    })).data,
    onSuccess: (r: any) => {
      const partes = [`Venda #${r.numero}: ${rotulo(r.forma_antiga)} → ${rotulo(r.forma_nova)}`]
      if (r.impacto_gaveta) {
        partes.push(`gaveta ${r.impacto_gaveta > 0 ? '+' : ''}${formatMoney(r.impacto_gaveta)}`)
      }
      if (r.transferencia) {
        partes.push(`${formatMoney(r.transferencia.valor)}: ${r.transferencia.conta_de} → ${r.transferencia.conta_para}`)
      }
      if (r.ajuste_fechamento) {
        const a = r.ajuste_fechamento
        toast(`Fechamento do caixa #${a.sessao_id} ajustado: esperado ${formatMoney(a.valor_sistema_de)} → ${formatMoney(a.valor_sistema_para)}, diferença ${formatMoney(a.diferenca_de)} → ${formatMoney(a.diferenca_para)}`,
          { icon: '🧾', duration: 10000 })
      }
      if (!r.titulo_encontrado) {
        toast('Forma trocada no pedido, mas não achei o título dessa linha — confira em Contas a Receber.',
          { icon: '⚠️', duration: 8000 })
      }
      toast.success(partes.join(' · '), { duration: 7000 })
      fecharModal()
      qc.invalidateQueries({ queryKey: ['correcao-forma-vendas'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao corrigir', { duration: 8000 }),
  })

  const podeSalvar = !!formaNova && motivo.trim().length > 0 && senha.length > 0 && !corrigir.isPending

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ArrowRightLeft className="w-6 h-6" /> Corrigir Forma de Recebimento
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Venda do PDV lançada na forma errada. A troca ajusta o fechamento de caixa, o título
          em Contas a Receber e o saldo das contas bancárias — tudo de uma vez.
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 flex flex-wrap gap-3 items-end shadow-sm">
        <div>
          <label className="block text-xs text-gray-500 mb-1">De</label>
          <input type="date" className="input" value={inicio} onChange={e => setInicio(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Até</label>
          <input type="date" className="input" value={fim} onChange={e => setFim(e.target.value)} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs text-gray-500 mb-1">Nº da venda ou cliente</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="input pl-8 w-full" placeholder="Nº da venda (ignora o período) ou nome do cliente"
              value={busca} onChange={e => setBusca(e.target.value)} />
          </div>
        </div>
      </div>

      {buscaNumero && (
        <div className="text-sm rounded p-3 bg-blue-50 text-blue-900 flex gap-2">
          <Search className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Buscando a venda <strong>#{busca.trim()}</strong> em qualquer data — o filtro de período está sendo ignorado.</span>
        </div>
      )}

      {truncado && !buscaNumero && (
        <div className="text-sm rounded p-3 bg-amber-50 text-amber-900 flex gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            Mostrando as <strong>{LIMITE} vendas mais recentes</strong> do período — pode haver
            mais. Estreite as datas, ou busque direto pelo número da venda.
          </span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-gray-500 p-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando vendas…
        </div>
      ) : vendas.length === 0 ? (
        <div className="text-center text-gray-500 p-10 bg-white dark:bg-gray-800 rounded-lg">
          Nenhuma venda do PDV no período.
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg overflow-x-auto shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-left">
              <tr>
                <th className="p-3">Venda</th>
                <th className="p-3">Data</th>
                <th className="p-3">Cliente</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3">Formas recebidas</th>
                <th className="p-3">Caixa</th>
              </tr>
            </thead>
            <tbody>
              {vendas.map(v => (
                <tr key={v.pedido_id} className="border-t dark:border-gray-700">
                  <td className="p-3 font-medium">#{v.numero}</td>
                  <td className="p-3 whitespace-nowrap">
                    {v.data ? new Date(v.data).toLocaleString('pt-BR') : '—'}
                  </td>
                  <td className="p-3">{v.cliente}</td>
                  <td className="p-3 text-right font-medium">{formatMoney(v.total)}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-2">
                      {v.pagamentos.map(l => (
                        <button
                          key={l.indice}
                          disabled={!l.corrigivel}
                          onClick={() => { setAlvo({ venda: v, linha: l }); setFormaNova('') }}
                          title={
                            l.corrigivel
                              ? (v.sessao_status === 'fechada'
                                  ? 'Caixa já fechado — a correção ajusta o fechamento junto'
                                  : 'Clique para corrigir esta forma')
                              : 'Forma a prazo/estrutural — cancele a venda e refaça'
                          }
                          className={`px-2 py-1 rounded border text-xs flex items-center gap-1 ${
                            l.corrigivel
                              ? 'border-blue-300 text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                              : 'border-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {!l.corrigivel && <Lock className="w-3 h-3" />}
                          {rotulo(l.forma)} · {formatMoney(l.valor)}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="p-3">
                    {v.sessao_id ? (
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        v.sessao_status === 'aberta'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        #{v.sessao_id} {v.sessao_status}
                      </span>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {alvo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={e => { if (e.target === e.currentTarget) fecharModal() }}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-5 w-full max-w-md space-y-4">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5" /> Corrigir forma — venda #{alvo.venda.numero}
            </h2>

            <div className="bg-gray-50 dark:bg-gray-700/40 rounded p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-500">Forma atual</span>
                <span className="font-medium">{rotulo(alvo.linha.forma)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Valor</span>
                <span className="font-medium">{formatMoney(alvo.linha.valor)}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Nova forma *</label>
              <select className="input w-full" value={formaNova} onChange={e => setFormaNova(e.target.value)}>
                <option value="">Selecione…</option>
                {formas.filter(f => f.forma !== alvo.linha.forma).map(f => (
                  <option key={f.forma} value={f.forma}>
                    {f.label}{f.conta_nome ? ` → ${f.conta_nome}` : ''}
                  </option>
                ))}
              </select>
            </div>

            {formaNova && (() => {
              const de = formas.find(f => f.forma === alvo.linha.forma)
              const para = formas.find(f => f.forma === formaNova)
              if (de?.entra_na_gaveta === para?.entra_na_gaveta) return null
              const sai = de?.entra_na_gaveta
              return (
                <div className={`text-sm rounded p-3 flex gap-2 ${
                  sai ? 'bg-amber-50 text-amber-900' : 'bg-emerald-50 text-emerald-900'
                }`}>
                  <Banknote className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    O esperado em <strong>dinheiro na gaveta</strong> vai{' '}
                    {sai ? 'DIMINUIR' : 'AUMENTAR'} {formatMoney(alvo.linha.valor)} no fechamento
                    deste caixa.
                    {alvo.venda.sessao_status === 'fechada' && (
                      <>
                        {' '}Como o caixa <strong>#{alvo.venda.sessao_id} já está fechado</strong>,
                        o valor esperado e a diferença daquele fechamento serão recalculados, e a
                        sobra/falta no DRE será refeita.
                      </>
                    )}
                  </span>
                </div>
              )
            })()}

            {alvo.venda.sessao_status === 'fechada' && (
              <div className="text-sm rounded p-3 flex gap-2 bg-blue-50 text-blue-900">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  Venda de <strong>caixa fechado</strong> (#{alvo.venda.sessao_id}). A correção fica
                  registrada no fechamento, com motivo e quem autorizou.
                </span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Motivo *</label>
              <input className="input w-full" value={motivo} onChange={e => setMotivo(e.target.value)}
                placeholder="Ex.: operador lançou dinheiro, cliente pagou no cartão" />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Senha de autorização *</label>
              <input type="password" className="input w-full" value={senha}
                onChange={e => setSenha(e.target.value)} autoComplete="new-password" />
              <p className="text-xs text-gray-500 mt-1 flex gap-1">
                <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                Exige cargo (admin, gerente ou financeiro). Sem cargo, use a senha de um supervisor.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button className="btn-secondary" onClick={fecharModal}>Cancelar</button>
              <button className="btn-primary flex items-center gap-2" disabled={!podeSalvar}
                onClick={() => corrigir.mutate()}>
                {corrigir.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Corrigir forma
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
